import { FC, useEffect, useState, useRef } from 'react';
import { useSpring, animated } from '@react-spring/web';
import cx from 'classnames';
import styles from './Swatch.module.scss';
import A11ySwatches from '../A11ySwatches';
import { useFormikContext, Field } from 'formik';
import colorMethods from '../../../utils/color';
import { useColorRamp } from '../ColorRampContext';

export interface SwatchProps {
  colors: any[];
  rootName: string;
  colorIdx: number;
  hexDisabled?: boolean;
  id: string;
}

interface SwatchFormProps {
  rootName: string;
  colorIdx: number;
  hexDisabled?: boolean;
  formColor: string;
}

const SwatchForm: FC<SwatchFormProps> = ({ rootName, colorIdx, hexDisabled, formColor }) => {
  const { values, setFieldValue } = useFormikContext();
  const [isLoaded, setIsLoaded] = useState(false);

  // @ts-ignore
  const hsl = values[rootName][colorIdx].hsl;
  // @ts-ignore
  const hex = values[rootName][colorIdx].hex;

  useEffect(() => {
    if (isLoaded) {
      setFieldValue(`${rootName}.${colorIdx}.hex`, colorMethods.hslToHex(...hsl));
      setFieldValue(`${rootName}.${colorIdx}.rgb`, colorMethods.hslToRgbArray(...hsl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hsl]);

  useEffect(() => {
    if (isLoaded && hex.length === 7) {
      // This causes an infinite loop because of the hsl effect above.
      // Need to create a stateful value 'activeColorSpace' that is set onBlur
      // setFieldValue(`${rootName}.${colorIdx}.hsl`, colorMethods.hslArrayFromHex(hex));
      setFieldValue(`${rootName}.${colorIdx}.rgb`, colorMethods.rgbArrayFromHex(hex));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hex]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <>
      <div className='formControl'>
        <label
          className={cx('formControl__label', {
            'formControl__label--dark': formColor === '#000000',
          })}
        >
          Hex
        </label>
        <Field className='formControl__input' type='text' name={`${rootName}.${colorIdx}.hex`} />
      </div>
      <div className='formControl'>
        <label className='formControl__label'>HSL</label>
        <div className='formControl__flex'>
          <div className='formControl__flexItem'>
            <Field className='formControl__input' type='number' name={`${rootName}.${colorIdx}.hsl[0]`} />
            <span className='formControl__sublabel'>Hue</span>
          </div>
          <div className='formControl__flexItem'>
            <Field className='formControl__input' type='number' name={`${rootName}.${colorIdx}.hsl[1]`} />
            <span className='formControl__sublabel'>Sat.</span>
          </div>
          <div className='formControl__flexItem'>
            <Field className='formControl__input' type='number' name={`${rootName}.${colorIdx}.hsl[2]`} />
            <span className='formControl__sublabel'>Light.</span>
          </div>
        </div>
      </div>
      <div className='formControl'>
        <label
          className={cx('formControl__label', {
            'formControl__label--disabled': hexDisabled,
            'formControl__label--dark': formColor === '#000000',
          })}
        >
          RGB
        </label>
        <div className='formControl__flex'>
          <div className='formControl__flexItem'>
            <Field
              disabled={true}
              className='formControl__input'
              type='number'
              name={`${rootName}.${colorIdx}.rgb[0]`}
            />
            <span className='formControl__sublabel'>Red</span>
          </div>
          <div className='formControl__flexItem'>
            <Field
              disabled={true}
              className='formControl__input'
              type='number'
              name={`${rootName}.${colorIdx}.rgb[1]`}
            />
            <span className='formControl__sublabel'>Green</span>
          </div>
          <div className='formControl__flexItem'>
            <Field
              disabled={true}
              className='formControl__input'
              type='number'
              name={`${rootName}.${colorIdx}.rgb[2]`}
            />
            <span className='formControl__sublabel'>Blue</span>
          </div>
        </div>
      </div>
    </>
  );
};

export const Swatch: FC<SwatchProps> = ({ colors, rootName, colorIdx, hexDisabled, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [formColor, setFormColor] = useState('#000000');

  const { showContrast, rampId } = useColorRamp();

  function toggleOpen() {
    setIsOpen(!isOpen);
  }

  const colorsRef = useRef<any[]>([]);

  useEffect(() => {
    // @ts-ignore
    colorsRef.current = [].concat(colors);
    if (colors && colorMethods.contrast(colors[colorIdx].rgb, [255, 255, 255], 4.5)) {
      setFormColor('#ffffff');
    }
  }, [colors]);

  useEffect(() => {
    setIsLoaded(true);
    // if (colors && colorMethods.contrast(colors[colorIdx].rgb, [255, 255, 255], 4.5)) {
    //   console.log(colors[colorIdx].rgb);
    //   setFormColor('#ffffff');
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { swatchWidth } = useSpring({
    swatchWidth: isOpen ? '240px' : '40px',
  });

  return (
    <div>
      <h4 className={styles.name}>
        {`${colorIdx + 1}0`}
        {/* {colorIdx > 0 && colorIdx < colors.length - 1 ? `${colorIdx}0` : colorIdx === 0 ? 'white' : 'black'} */}
      </h4>
      <div className={cx(styles.swatch, { [styles['swatch--light']]: formColor === '#ffffff' })}>
        {isLoaded && (
          <>
            <div className={styles.swatch__clickZone} onClick={toggleOpen} />

            <animated.div
              className={styles.swatch__colorZone}
              // @ts-ignore
              style={{
                width: swatchWidth,
                background: `hsl(${colors[colorIdx]?.hsl[0]}, ${colors[colorIdx]?.hsl[1]}%, ${colors[colorIdx]?.hsl[2]}%)`,
              }}
            >
              <animated.div
                className={styles.form}
                style={{
                  transform: isOpen ? `translate3d(0px, 0%, 0)` : `translate3d(-20px, 0, 0)`,
                  opacity: isOpen ? 1 : 0,
                }}
              >
                <div className={styles.form__inner}>
                  <SwatchForm rootName={rootName} colorIdx={colorIdx} hexDisabled={hexDisabled} formColor={formColor} />
                </div>
              </animated.div>
            </animated.div>
            {showContrast && showContrast === `showUiContrast-${rampId}` && (
              <div
                style={{
                  position: 'absolute',
                  top: '30px',
                  right: 0,
                  zIndex: 10,
                  borderRadius: '4px',
                  background: `hsl(${colors[colorIdx]?.hsl[0]}, ${colors[colorIdx]?.hsl[1]}%, ${colors[colorIdx]?.hsl[2]}%)`,
                }}
              >
                <A11ySwatches id={id} type='ui' colors={colorsRef?.current} rootName={rootName} colorIdx={colorIdx} />
              </div>
            )}
            {showContrast && showContrast === `showTextContrast-${rampId}` && (
              <div
                style={{
                  position: 'absolute',
                  top: '30px',
                  right: 0,
                  zIndex: 10,
                  borderRadius: '4px',
                  background: `hsl(${colorsRef?.current?.[colorIdx]?.hsl[0]}, ${colorsRef?.current?.[colorIdx]?.hsl[1]}%, ${colorsRef?.current?.[colorIdx]?.hsl[2]}%)`,
                }}
              >
                <A11ySwatches id={id} type='text' colors={colorsRef?.current} rootName={rootName} colorIdx={colorIdx} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default Swatch;
